import { cn } from '@codeware/shared/util/ui';
import { type VariantProps, cva } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import * as React from 'react';

// Rounding follows the theme only at its extreme: a square theme squares the
// badge, any other radius multiplies past the clamp and stays a pill. That is
// how shadcn behaves, and it keeps a badge from reading as a small button.
const badgeVariants = cva(
  'group/badge inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-[min(calc(var(--radius)*99),9999px)] border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3!',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground [a]:hover:bg-primary/80',
        secondary:
          'bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80',
        destructive:
          'bg-destructive/10 text-(--destructive-subtle) focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:focus-visible:ring-destructive/40 [a]:hover:bg-destructive/20',
        outline:
          'border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground',
        ghost:
          'hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50',
        link: 'text-primary underline-offset-4 hover:underline',
        // `--success-subtle` and `--warning-subtle` exist in the theme template
        // for exactly this. Outlined rather than tinted like `destructive`,
        // which is what the call sites these replace already were.
        success:
          'border-(--success-subtle)/30 text-(--success-subtle) [a]:hover:bg-(--success-subtle)/10',
        // The fourth status alongside success, warning and destructive: a state
        // with nothing to report. `outline` says the shape, not the tone.
        muted: 'border-border text-muted-foreground [a]:hover:bg-muted',
        warning:
          'border-(--warning-subtle)/30 text-(--warning-subtle) [a]:hover:bg-(--warning-subtle)/10',
        // A status pill that reads as the brand. One style for every value —
        // the label carries the meaning, not the colour.
        brand:
          'border-brand-200 bg-brand-50 text-brand-700 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-200 [a]:hover:bg-brand-100 dark:[a]:hover:bg-brand-900'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
);

function Badge({
  className,
  variant = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span';

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
